import { useEffect } from "react";

import {
  Badge,
  Collapse,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { visuallyHidden } from "@mui/utils";
import PendingActionsIcon from "@mui/icons-material/PendingActions";

import conf from "config-dlm";
import useOpen from "components/utils/useOpen";
import Refresh from "components/utils/Refresh";
import usePagination from "hooks/pagination";
import EmptyTableRow from "components/rows/Empty";
import SectionHeader from "components/utils/SectionHeader";
import { PendingRow } from "./Pending";
import LinearIndeterminate from "components/utils/LinearLoader";
import { usePavmApprove, usePavmReject } from "hooks/pavm/requests";
import { RequestSchema, useUserServiceRequests } from "hooks/user/base";

export const RequestsPendingLite = () => {
  const { open, toggle, setOpen } = useOpen(false);

  const paginate = usePagination({ defaultSort: "updatedAt" });
  const { order, sort, page, limit, body } = paginate;
  const { handleChangeLimit, handleChangePage, handleSort } = paginate;

  const reject = usePavmReject();
  const approve = usePavmApprove();
  const pending = useUserServiceRequests({ status: "PENDING", ...body });

  useEffect(() => {
    const total = pending.data?.meta.total;
    if (total && total > 0) setOpen(true);
  }, [pending.data?.meta.total]);

  return (
    <Container maxWidth="xl">
      <SectionHeader
        title={"Requests"}
        icon={
          <Badge badgeContent={pending.data?.meta.total} color="error">
            <PendingActionsIcon />
          </Badge>
        }
        buttons={[
          <TablePagination
            key={"requests-pending-pagination"}
            component="div"
            rowsPerPageOptions={[10, 25, 50, 100]}
            count={pending?.data?.meta.total || 0}
            page={page}
            rowsPerPage={limit}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeLimit}
          />,
          <Refresh
            key={"requests-pending-refresh"}
            isFetching={pending.isFetching}
            onClick={pending.refetch}
          />,
          <IconButton
            key={"requests-pending-collapse"}
            size="small"
            onClick={toggle}
            aria-label="expand"
            sx={{
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.3s",
            }}
          >
            <ExpandMoreIcon />
          </IconButton>,
        ]}
      />

      <Collapse in={open}>
        <TableContainer sx={{ boxShadow: 4, mb: 4, maxHeight: 630 }}>
          <Table aria-label="collapsible table" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell />

                {conf.COLUMNS.REQUESTS.PENDING.map((column) => (
                  <TableCell
                    key={column.key}
                    sortDirection={order}
                    align={column.isHeader ? "left" : "right"}
                  >
                    <TableSortLabel
                      active={sort === column.key}
                      direction={sort === column.key ? order : "asc"}
                      onClick={(event: React.MouseEvent<unknown>) =>
                        handleSort(event, column.key)
                      }
                    >
                      <b>{column.label}</b>
                      {sort === column.key ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {pending.isLoading ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <LinearIndeterminate />
                  </TableCell>
                </TableRow>
              ) : pending.data?.data.length === 0 ? (
                <EmptyTableRow colSpan={7} type="requests" />
              ) : (
                pending.data?.data?.map((request: RequestSchema) => (
                  <PendingRow
                    key={request.id}
                    reject={reject}
                    approve={approve}
                    request={request}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Container>
  );
};
